import { useId } from "react";

import type { Theme } from "../theme";

type HeaderProps = {
  isAudioMuted: boolean;
  roundNumber: number;
  score: number;
  theme: Theme;
  onOpenSettings: () => void;
  onToggleAudioMuted: () => void;
};

function GearIcon() {
  const maskId = useId().replace(/:/g, "");

  return (
    <svg className="gear-icon" viewBox="0 0 24 24" aria-hidden="true">
      <mask id={maskId} x="0" y="0" width="24" height="24" maskUnits="userSpaceOnUse">
        <rect width="24" height="24" fill="#fff" />
        <circle cx="12" cy="12" r="3.15" fill="#000" />
      </mask>
      <g fill="currentColor" mask={`url(#${maskId})`}>
        <circle cx="12" cy="12" r="6.6" />
        <rect x="10.85" y="1.2" width="2.3" height="4.8" rx="1.15" />
        <rect x="18" y="10.85" width="4.8" height="2.3" rx="1.15" />
        <rect x="10.85" y="18" width="2.3" height="4.8" rx="1.15" />
        <rect x="1.2" y="10.85" width="4.8" height="2.3" rx="1.15" />
        <rect x="10.95" y="1.55" width="2.1" height="4.35" rx="1.05" transform="rotate(45 12 12)" />
        <rect x="10.95" y="1.55" width="2.1" height="4.35" rx="1.05" transform="rotate(135 12 12)" />
        <rect x="10.95" y="1.55" width="2.1" height="4.35" rx="1.05" transform="rotate(225 12 12)" />
        <rect x="10.95" y="1.55" width="2.1" height="4.35" rx="1.05" transform="rotate(315 12 12)" />
      </g>
    </svg>
  );
}

function SpeakerIcon({ muted }: { muted: boolean }) {
  return (
    <svg className="speaker-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M5 9.75h3.25l4.55-3.8a.9.9 0 0 1 1.48.69v10.72a.9.9 0 0 1-1.48.69l-4.55-3.8H5a1 1 0 0 1-1-1v-2.5a1 1 0 0 1 1-1Z"
        fill="currentColor"
      />
      {muted ? (
        <path
          d="m17.2 8.1 3.7 3.9m0-3.9-3.7 3.9"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth="2"
        />
      ) : (
        <>
          <path
            d="M17.4 9.1a4.1 4.1 0 0 1 0 5.8"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeWidth="2"
          />
          <path
            d="M19.95 6.6a7.65 7.65 0 0 1 0 10.8"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeWidth="2"
          />
        </>
      )}
    </svg>
  );
}

function formatScore(score: number): string {
  return score.toLocaleString();
}

export function Header({
  isAudioMuted,
  roundNumber,
  score,
  theme,
  onOpenSettings,
  onToggleAudioMuted
}: HeaderProps) {
  return (
    <header className="top-bar">
      <div className="top-bar-side top-bar-side-start">
        <div className={`top-bar-stat top-bar-stat-round panel panel-${theme}`}>
          <p className="stat-label">Round</p>
          <p className="stat-value">{roundNumber}</p>
        </div>
      </div>
      <div className="top-bar-center">
        <div className={`top-bar-stat top-bar-stat-score panel panel-${theme}`}>
          <p className="stat-label">Score</p>
          <p className="stat-value">{formatScore(score)}</p>
        </div>
      </div>
      <div className="top-bar-side top-bar-side-end">
        <div className="top-bar-controls">
          <button
            type="button"
            className="icon-button icon-button-square top-bar-icon"
            onClick={onOpenSettings}
            aria-label="Open settings"
          >
            <GearIcon />
          </button>
          <button
            type="button"
            className={`icon-button icon-button-square top-bar-icon top-bar-mute-button${
              isAudioMuted ? " is-muted" : ""
            }`}
            onClick={onToggleAudioMuted}
            aria-label={isAudioMuted ? "Unmute audio" : "Mute audio"}
            aria-pressed={isAudioMuted}
          >
            <SpeakerIcon muted={isAudioMuted} />
          </button>
        </div>
      </div>
    </header>
  );
}
